import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createHeaders, getJWT, isAuthenticated, apiGet, API_BASE_URL } from '../auth';
import { useLanguage } from '../contexts/LanguageContext';
import { AssignSurveyorModal } from '../components/AssignSurveyorModal';
import { 
  ArrowLeft, FileText, CheckCircle, Clock, 
  Download, Link, UploadCloud, UserPlus, 
  MapPin, Activity, ShieldCheck, FileDown, Search, Plus
} from 'lucide-react';

interface SurveyHistoryData {
  report_id: string;
  project_description: string;
  cargo_weight_mt: number;
  file_path: string;
  created_at: string;
  guest_token?: string;
  survey_status: 'pending' | 'submitted';
  assignment_status?: string;
  surveyor_name?: string;
  surveyor_pdf_url?: string;
}

const API_URL = API_BASE_URL;

const Surveys: React.FC = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<SurveyHistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, lang } = useLanguage();
  
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string>('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    if (!getJWT()) return; // Prevent ghost fetches during tear-down
    try {
      const data = await apiGet<SurveyHistoryData[]>('api/surveys/history');
      setHistory(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyGuestLink = (guest_token: string | undefined) => {
    if (!guest_token) {
      alert(t('surveys.old_report_error'));
      return;
    }
    const link = `${window.location.origin}/submit-report/${guest_token}`;
    navigator.clipboard.writeText(link);
    alert(t('surveys.send_link') + ':\n' + link);
  };

  const downloadFinalPdf = async (report_id: string) => {
    setIsDownloading(report_id);
    try {
      const isAuth = await isAuthenticated();
      if (!isAuth) throw new Error(t('surveys.session_expired'));
      const response = await fetch(`${API_URL}/api/surveys/pdf/${report_id}`, {
        headers: createHeaders(getJWT())
      });
      const item = history.find(h => h.report_id === report_id);
      const isSurvey = item?.survey_status === 'submitted';
      const filename = isSurvey ? `${report_id}_w_pictures.pdf` : `Lashing_Plan_${report_id}.pdf`;
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(t('surveys.download_failed') + error.message);
    } finally {
      setIsDownloading(null);
    }
  };

  const downloadSurveyorPdf = async (report_id: string) => {
    try {
      const token = localStorage.getItem('jwt');
      const proxyUrl = API_URL.replace('8000', '8001') + `/api/surveys/download_surveyor_pdf/${report_id}`;
      const res = await fetch(proxyUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to download Proxy PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Surveyor_Report_${report_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch(e) {
      alert(t('surveys.download_failed'));
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex justify-center items-center">
      <div className="flex flex-col items-center gap-3">
         <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
         <span className="text-slate-500 font-medium">{t('surveys.connecting')}</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F9FAFB] p-8 flex justify-center items-center">
      <div className="bg-red-50 text-red-600 p-6 rounded-lg shadow-sm border border-red-100 max-w-md text-center">
        <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-red-500" />
        <h3 className="font-semibold text-lg mb-2">{t('surveys.access_error')}</h3>
        <p className="text-sm">{error}</p>
      </div>
    </div>
  );

  const pendingPhotosCount = history.filter(h => h.survey_status === 'pending').length;
  const completedAssignmentsCount = history.filter(h => h.assignment_status === 'completed').length;
  const totalCount = history.length;

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-slate-900 font-sans pb-20">
      
      {/* HEADER NAV */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/app')}
              title={t('common.cancel')}
              className="p-2 -ml-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center">
                 <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-800">{t('surveys.title')}</h1>
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/app')}
            className="hidden sm:flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium text-sm shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('surveys.create_new')}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* METRICS ROW */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between group cursor-default hover:border-indigo-200 transition-colors">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('surveys.total_ops')}</p>
              <h3 className="text-3xl font-bold tracking-tight text-slate-800">{totalCount}</h3>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
              <FileText className="w-6 h-6" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between group cursor-default hover:border-amber-200 transition-colors">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('surveys.pending_photos')}</p>
              <h3 className="text-3xl font-bold tracking-tight text-slate-800">{pendingPhotosCount}</h3>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-amber-600 group-hover:bg-amber-500 group-hover:text-white transition-colors">
              <Clock className="w-6 h-6" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between group cursor-default hover:border-emerald-200 transition-colors">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('surveys.completed_assignments')}</p>
              <h3 className="text-3xl font-bold tracking-tight text-slate-800">{completedAssignmentsCount}</h3>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* LIST / TABLE AREA */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center bg-slate-50/50">
            <h2 className="text-lg font-medium text-slate-800">{t('surveys.current_reports')}</h2>
            <div className="relative">
               <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
               <input 
                 type="text" 
                 placeholder={t('surveys.search_placeholder')} 
                 className="pl-9 pr-4 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-64 bg-white"
               />
             </div>
          </div>
          
          {history.length === 0 ? (
             <div className="p-12 text-center flex flex-col items-center">
                <FileText className="w-16 h-16 text-slate-200 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">{t('surveys.no_reports_title')}</h3>
                <p className="text-slate-500 text-sm max-w-sm mb-6">{t('surveys.no_reports_desc')}</p>
                <button 
                  onClick={() => navigate('/app')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md font-medium shadow-sm transition-colors"
                >
                  {t('surveys.create_now')}
                </button>
             </div>
          ) : (
             <div className="divide-y divide-slate-100">
               {history.map((item) => (
                 <div key={item.report_id} className="p-6 flex flex-col lg:flex-row xl:items-center justify-between gap-6 hover:bg-slate-50/50 transition-colors">
                    
                    {/* LEFT: Info */}
                    <div className="flex items-start gap-4 lg:w-4/12">
                      <div className="mt-1 p-2 bg-slate-100 rounded border border-slate-200 text-slate-500 shrink-0">
                        <Activity className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                           {item.report_id}
                        </div>
                        <div className="text-sm text-slate-500 mt-1 line-clamp-2" title={item.project_description || t('surveys.unnamed_project')}>{item.project_description || t('surveys.unnamed_project')}</div>
                        <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5" /> {t('surveys.created_date')}: {new Date(item.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>

                    {/* MIDDLE: Status Badges */}
                    <div className="flex flex-col gap-3 lg:w-4/12 justify-center">
                       <div className="flex items-center gap-2">
                         <span className="text-xs font-medium text-slate-400 w-20 tracking-wider uppercase">{t('surveys.data_status')}</span>
                         {item.survey_status === 'submitted' ? (
                           <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                             <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {t('surveys.uploaded')}
                           </span>
                         ) : (
                           <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                             <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span> {t('surveys.waiting')}
                           </span>
                         )}
                       </div>
                       
                       {item.assignment_status && (
                         <div className="flex items-center gap-2">
                           <span className="text-xs font-medium text-slate-400 w-20 truncate tracking-wider uppercase" title={item.surveyor_name}>{t('surveys.surveyor')}</span>
                           {item.assignment_status === 'pending' && (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200" title={item.surveyor_name}>
                               <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse"></span> {t('surveys.waiting_response')}
                             </span>
                           )}
                           {item.assignment_status === 'accepted' && (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" title={item.surveyor_name}>
                               <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span> {t('surveys.under_review')}
                             </span>
                           )}
                           {item.assignment_status === 'completed' && (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" title={item.surveyor_name}>
                               <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {t('surveys.report_ready')}
                             </span>
                           )}
                           {item.assignment_status === 'rejected' && (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200" title={item.surveyor_name}>
                               <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> {t('surveys.cancelled_rejected')}
                             </span>
                           )}
                         </div>
                       )}
                    </div>

                    {/* RIGHT: Actions */}
                    <div className="flex flex-col sm:flex-row xl:flex-col items-start xl:items-end justify-between xl:justify-center gap-3 lg:w-4/12">
                      
                      {/* Secondary Actions Row */}
                      <div className="flex gap-2">
                        <button 
                          onClick={() => copyGuestLink(item.guest_token)}
                          title={t('surveys.send_link')}
                          className="p-2 text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:text-indigo-600 transition-colors shadow-sm"
                        >
                          <Link className="w-4 h-4" />
                        </button>
                        
                        <button 
                          onClick={() => {
                            if(!item.guest_token) alert(t('surveys.old_report_error'));
                            else window.open(`/submit-report/${item.guest_token}`, '_blank');
                          }}
                          title={t('surveys.upload_manually')}
                          className="p-2 text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:text-indigo-600 transition-colors shadow-sm"
                        >
                          <UploadCloud className="w-4 h-4" />
                        </button>

                        {(!item.assignment_status || item.assignment_status === 'rejected') ? (
                          <button 
                            onClick={() => {
                              setSelectedReportId(item.report_id);
                              setAssignModalOpen(true);
                            }}
                            title={t('surveys.assign_new')}
                            className="px-3 py-2 text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors shadow-sm flex items-center gap-1.5"
                          >
                            <UserPlus className="w-4 h-4" />
                            <span className="text-xs font-medium">{t('surveys.assign_surveyor')}</span>
                          </button>
                        ) : (
                          <button 
                            disabled
                            className="px-3 py-2 text-slate-400 bg-slate-50 border border-slate-200 rounded-md shadow-sm flex items-center gap-1.5 cursor-not-allowed"
                            title={`${item.surveyor_name} ${t('surveys.assigned')}`}
                          >
                            <ShieldCheck className="w-4 h-4" />
                            <span className="text-xs font-medium">{t('surveys.assigned')}</span>
                          </button>
                        )}
                      </div>

                      {/* Primary Actions */}
                      <div className="flex gap-2 w-full xl:w-auto xl:justify-end mt-1 xl:mt-0">
                        <button 
                          onClick={() => downloadFinalPdf(item.report_id)}
                          disabled={isDownloading === item.report_id}
                          className="flex-1 xl:flex-none items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
                        >
                          <FileDown className="w-4 h-4 inline-block mr-1" />
                          {isDownloading === item.report_id ? t('surveys.wait') : t('surveys.summary_pdf')}
                        </button>
                        
                        {item.surveyor_pdf_url && (
                          <button 
                            onClick={() => downloadSurveyorPdf(item.report_id)}
                            className="flex-1 xl:flex-none items-center justify-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-md shadow-sm hover:bg-indigo-700 transition"
                          >
                            <Download className="w-4 h-4 inline-block mr-1" />
                            {t('surveys.surveyor_pdf')}
                          </button>
                        )}
                      </div>

                    </div>
                 </div>
               ))}
             </div>
          )}
        </div>
      </div>

      <AssignSurveyorModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        reportId={selectedReportId}
        onAssignSuccess={() => {
          setAssignModalOpen(false);
          alert(t('surveys.assignment_success'));
          fetchHistory();
        }}
      />
    </div>
  );
};

export default Surveys;
